import {
    Box, Button, ButtonGroup, Dialog, DialogActions, DialogContent, DialogTitle,
    TextField, Typography, IconButton, CircularProgress
} from '@mui/material';
import { CloudUpload as CloudUploadIcon, Description as DescriptionIcon } from '@mui/icons-material';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import React, { useEffect, useState, useRef } from 'react';
import AptaTable from 'examples/AptaTable';
import MDTypography from 'components/MDTypography';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Icon } from '@mui/material';
import { URL } from 'constants/constant';

const AbstractDoc = () => {
    const [selectedDoc, setSelectedDoc] = useState({ title: "", document: "" });
    const [open, setOpen] = useState(false);
    const [docs, setDocs] = useState([]);
    const [formType, setFormType] = useState('add');
    const [loading, setLoading] = useState(false);

    const handleOpen = (type, item = { title: "", document: "" }) => {
        setFormType(type);
        setSelectedDoc(item);
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    const getAbstracts = async () => {
        try {
            const res = await axios.get(`${URL}/api/admin/abstract`);
            if (res.data?.success) {
                setDocs(res.data.data.reverse());
            }
        } catch (err) {
            console.error("Fetch error:", err);
            Swal.fire("Error", "Could not fetch abstract documents", "error");
        }
    };

    const handleSave = async (data) => {
        try {
            setLoading(true);
            const formData = new FormData();
            formData.append("title", data.title);
            if (data.document instanceof File) {
                formData.append("document", data.document);
            }

            if (formType === 'add') {
                await axios.post(`${URL}/api/admin/abstract`, formData);
                Swal.fire("Success", "Document added successfully", "success");
            } else {
                await axios.patch(`${URL}/api/admin/abstract/${data._id}`, formData);
                Swal.fire("Success", "Document updated successfully", "success");
            }

            handleClose();
            getAbstracts();
        } catch (err) {
            console.error("Save error:", err);
            Swal.fire("Error", "Document could not be saved", "error");
            handleClose();
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axios.delete(`${URL}/api/admin/abstract/${id}`);
                    Swal.fire("Deleted!", "The document has been deleted.", "success");
                    getAbstracts();
                } catch (err) {
                    console.error("Delete error:", err);
                    Swal.fire("Error", "Could not delete document", "error");
                }
            }
        });
    };

    useEffect(() => {
        getAbstracts();
    }, []);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            {/* <Box display="flex" justifyContent="end" mb={3}>
                <Button variant="contained" color="primary" startIcon={<Icon>add</Icon>} onClick={() => handleOpen('add')} style={{ color: 'white' }}>
                    Add Abstract Document
                </Button>
            </Box> */}

            <AptaTable
                columns={['Title', 'File', 'Actions']}
                rows={docs?.map((item) => ({
                    Title: <MDTypography variant="caption">{item?.title}</MDTypography>,
                    File: item?.document ? (
                        <a href={`${URL}/${item.document}`} target="_blank" rel="noopener noreferrer">
                            <IconButton color="primary">
                                <DescriptionIcon />
                            </IconButton>
                        </a>
                    ) : null,

                    Actions: (
                        <ButtonGroup size="small">
                            <Button color="info" onClick={() => handleOpen('edit', item)}>
                                <Icon color="info">edit</Icon>
                            </Button>
                            {/* <Button color="error" onClick={() => handleDelete(item?._id)}>
                                <Icon color="error">delete</Icon>
                            </Button> */}
                        </ButtonGroup>
                    )
                }))}
            />

            <InputFormModal
                open={open}
                onClose={handleClose}
                onSave={handleSave}
                details={selectedDoc}
                type={formType}
                loading={loading}
            />
        </DashboardLayout>
    );
};

export default AbstractDoc;

const InputFormModal = ({ open, onClose, onSave, details, type, loading }) => {
    const [formData, setFormData] = useState({ title: "", document: "" });
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (details) {
            setFormData(details);
        }
    }, [details]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData({ ...formData, document: file });
        }
    };

    const isSaveDisabled = () => {
        return !formData.title || (!formData.document && type === 'add') || loading;
    };

    return (
        <Dialog open={open} maxWidth="sm" fullWidth onClose={onClose}>
            <DialogTitle>{type === 'add' ? 'Add Document' : 'Edit Document'}</DialogTitle>
            <DialogContent>
                <Box component="form" noValidate autoComplete="off" sx={{ mt: 1 }}>
                    <TextField
                        label="Title"
                        name="title"
                        value={formData.title || ''}
                        onChange={handleChange}
                        fullWidth
                        margin="normal"
                        disabled={loading}
                    />
                    <Box sx={{ mt: 2, mb: 2 }}>
                        <Typography variant="body2" color="textSecondary">
                            Upload PDF file only:
                        </Typography>
                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={<CloudUploadIcon />}
                            disabled={loading}
                        >
                            Choose PDF
                            <input
                                type="file"
                                accept="application/pdf"
                                hidden
                                ref={fileInputRef}
                                onChange={handleFileChange}
                            />
                        </Button>

                        {formData.document && typeof formData.document !== 'string' && (
                            <Typography variant="caption" color="textSecondary">
                                Selected file: {formData.document.name}
                            </Typography>
                        )}

                        {type === 'edit' && typeof formData.document === 'string' && (
                            <Typography variant="caption" color="textSecondary">
                                Current file:&nbsp;
                                <a href={`${URL}/${formData.document}`} target="_blank" rel="noopener noreferrer">
                                    {formData.document.split('/').pop()}
                                </a>
                            </Typography>
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} color="primary" disabled={loading}>
                    Cancel
                </Button>
                {loading ? (
                    <Box sx={{ px: 2, py: 1 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <Button
                        onClick={() => onSave(formData)}
                        color="primary"
                        variant="contained"
                        disabled={isSaveDisabled()}
                        style={{ color: 'white' }}
                    >
                        Save
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};
